import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import { isRuntimeBlockId } from '../../../utils/runTimeBlocks';

type CombinedBlocksState = Record<string, BlockStateUnionType | undefined> & {
  runtimeBlocks?: Record<string, BlockStateUnionType | undefined>;
};

// Shared across ALL ContextBuilder instances. Every block on a page owns its own
// builder (useComputeContextBuilder creates one per hook), so with only the builders'
// per-instance one-slot caches, one store write makes every block redo this O(N)
// merge — O(N²) per write, which pegged the CPU while scrolling repeatable lists
// (each row mount/unmount is a write). Keying purely on the two input refs makes
// sharing safe: same refs → same content, so cross-page-instance bleed is impossible,
// and the store updates both maps immutably so a stale ref can never be revalidated.
// Callers must keep treating the result as immutable — it is shared page-wide.
const combinedBlocksStateCache = new WeakMap<object, WeakMap<object, CombinedBlocksState>>();

export function getCombinedBlockMethodsInBlocksState({
  blocksMethods,
  blocksState,
}: {
  blocksState: Record<string, BlockStateUnionType | undefined>;
  blocksMethods: Record<string, unknown>;
}) {
  let cacheByMethods = combinedBlocksStateCache.get(blocksState);
  const cachedResult = cacheByMethods?.get(blocksMethods);
  if (cachedResult) {
    return cachedResult;
  }

  const blockStateKeys = Object.keys(blocksState);
  const blockStateKeysLength = blockStateKeys.length;
  const updatedBlocksState: Record<string, BlockStateUnionType | undefined> & {
    runtimeBlocks?: Record<string, BlockStateUnionType | undefined>;
  } = {};
  const runtimeBlocks: Record<string, BlockStateUnionType | undefined> = {};

  for (let i = 0; i < blockStateKeysLength; i++) {
    const blockId = blockStateKeys[i];
    if (isRuntimeBlockId(blockId)) {
      runtimeBlocks[blockId] = {
        ...blocksState[blockId],
        ...(blocksMethods[blockId] ?? {}),
      } as BlockStateUnionType;
    } else {
      updatedBlocksState[blockId] = {
        ...blocksState[blockId],
        ...(blocksMethods[blockId] ?? {}),
      } as BlockStateUnionType;
    }
  }

  updatedBlocksState.runtimeBlocks = runtimeBlocks;

  if (!cacheByMethods) {
    cacheByMethods = new WeakMap();
    combinedBlocksStateCache.set(blocksState, cacheByMethods);
  }
  cacheByMethods.set(blocksMethods, updatedBlocksState);

  return updatedBlocksState;
}
